import { Users, Baby, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const PRICE_PER_CHILD = 10;
const MIN_CHILDREN = 50;
const MIN_CHARGE = 500;

interface Props {
  childrenCount: number;
  isCurrentModel: boolean;
  onSelect: () => void;
}

const PerChildCard = ({ childrenCount, isCurrentModel, onSelect }: Props) => {
  const calculatedAmount = Math.max(childrenCount * PRICE_PER_CHILD, MIN_CHARGE);
  const isMinApplied = childrenCount < MIN_CHILDREN;

  return (
    <Card className="border-2 border-emerald-500/50 bg-gradient-to-br from-emerald-50/50 to-background dark:from-emerald-950/20">
      <CardHeader className="text-center pb-2">
        <div className="flex justify-center mb-2">
          <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <Baby className="w-6 h-6 text-emerald-600" />
          </div>
        </div>
        <Badge variant="outline" className="mx-auto mb-1 text-emerald-700 border-emerald-300">
          👶 تسعير مرن
        </Badge>
        <CardTitle className="text-xl">اشتراك بالطفل</CardTitle>
        <p className="text-sm text-muted-foreground">ادفع فقط حسب عدد الأطفال المسجلين</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Price */}
        <div className="text-center">
          <span className="text-3xl font-bold text-emerald-600">{PRICE_PER_CHILD}</span>
          <span className="text-sm text-muted-foreground mr-1">ج.م / طفل / شهرياً</span>
        </div>

        {/* Current children count */}
        <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5">
              <Users className="w-4 h-4 text-muted-foreground" />
              الأطفال المسجلين حالياً
            </span>
            <span className="font-bold">{childrenCount} طفل</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
              التكلفة المتوقعة
            </span>
            <span className="font-bold text-emerald-600">{calculatedAmount.toLocaleString()} ج.م</span>
          </div>
          {isMinApplied && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              ⚠️ الحد الأدنى {MIN_CHARGE} ج.م ({MIN_CHILDREN} طفل) — يُطبق تلقائياً
            </p>
          )}
        </div>

        {/* Features */}
        <ul className="space-y-1.5 text-sm">
          {[
            "يتغير تلقائياً مع إضافة/حذف الأطفال",
            "الحد الأدنى: 50 طفل (500 ج.م)",
            "يشمل كل مميزات النظام الإداري",
            "مناسب للحضانات المتغيرة الأعداد",
          ].map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">✓</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <Button
          className="w-full"
          variant={isCurrentModel ? "outline" : "default"}
          disabled={isCurrentModel}
          onClick={onSelect}
        >
          {isCurrentModel ? "الاشتراك الحالي ✓" : "اشترك بالطفل"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default PerChildCard;
export { PRICE_PER_CHILD, MIN_CHILDREN, MIN_CHARGE };
